import type {
  PublicCategoryStatus,
  PublicPlatform,
  PublicStatusResponse,
  PublicSummary,
} from "@/v2-status-client/status/status.types"
import {
  buildHistory,
  buildServiceList,
} from "@/v2-status-client/status/status.demo.utils"

// In-memory fixture used by `?demo=1` so the page can be reviewed
// without the public API up. Lives next to the hook that consumes
// it; never bundled into the production page beyond the bundler's
// dead-code elimination because the consumer guard checks the URL
// at runtime.
//
// The fixture matches the PublicStatusResponse shape one-for-one so
// the same render path covers both demo and live data. Numbers are
// picked to look realistic without telling a story that implies a
// live incident. Helpers in `status.demo.utils.ts` build the
// repetitive bits (30-day strips, latency sparklines, service rows).

const allOperational = buildHistory(
  Array.from({ length: 30 }, () => "operational" as const),
)

// Carve out a small "we wobbled three days ago" pattern so the
// strip + incidents list have something to look at without the
// page becoming a wall of red.
const slightlyWobblyHistory = buildHistory(
  Array.from({ length: 30 }, (_, i) =>
    i === 26 ? "degraded" : i === 28 ? "down" : "operational",
  ),
)

const baseCategories: PublicCategoryStatus[] = [
  {
    id: "placepoint-v2-apps",
    platformId: "placepoint-v2",
    name: "Apps",
    description: "Placepoint web app, console, and the services they call",
    status: "operational",
    summary: "All systems operational",
    operational: 18,
    degraded: 0,
    down: 0,
    totalCount: 18,
    history: allOperational,
    uptimePercent30d: 99.98,
    services: buildServiceList("apps", 18, { provider: "Placepoint" }),
  },
  {
    id: "gis-basemaps",
    platformId: "placepoint-v2",
    name: "Basemaps",
    description: "Background map tiles rendered underneath your content layers",
    status: "operational",
    summary: "All systems operational",
    operational: 13,
    degraded: 0,
    down: 0,
    totalCount: 13,
    history: allOperational,
    uptimePercent30d: 99.99,
    services: buildServiceList("basemap", 13, { provider: "Geodata" }),
  },
  {
    id: "gis-navigation",
    platformId: "placepoint-v2",
    name: "Navigation Layers",
    description: "Top-level layer groups available in the map navigation menu",
    status: "operational",
    summary: "All systems operational",
    operational: 2,
    degraded: 0,
    down: 0,
    totalCount: 2,
    history: allOperational,
    uptimePercent30d: 100,
    services: buildServiceList("navlayer", 2, { provider: "Placepoint" }),
  },
  {
    id: "gis-map-layers",
    platformId: "placepoint-v2",
    name: "Map Layers",
    description: "Individual map layers available in the layer picker",
    status: "degraded",
    summary: "1 of 12 slower than usual",
    operational: 11,
    degraded: 1,
    down: 0,
    totalCount: 12,
    history: slightlyWobblyHistory,
    uptimePercent30d: 99.62,
    // Flat services list mirrors the backend's behaviour: even
    // when sub-categories are populated the parent still carries
    // every service so an older client (or a consumer that doesn't
    // know about sub-categories) can still render the row in full.
    services: buildServiceList("maplayer", 12, {
      degradedAt: [3],
      provider: "Kartverket",
    }),
    // Sub-categories mirror the v2-console layer-menu category
    // grid. The demo set is deliberately short (3 buckets) -- it
    // exercises the rendering path without requiring the demo to
    // fabricate the entire layer catalogue.
    subCategories: [
      {
        id: "gis-map-layers-property",
        platformId: "placepoint-v2",
        name: "Property",
        description: "Cadaster, ownership, and parcel layers",
        status: "operational",
        summary: "All systems operational",
        operational: 5,
        degraded: 0,
        down: 0,
        totalCount: 5,
        history: allOperational,
        uptimePercent30d: 99.98,
        services: buildServiceList("maplayer-property", 5, {
          provider: "Kartverket",
        }),
      },
      {
        id: "gis-map-layers-buildings",
        platformId: "placepoint-v2",
        name: "Buildings",
        description: "Building footprints, heights, and 3D models",
        status: "degraded",
        summary: "1 of 4 slower than usual",
        operational: 3,
        degraded: 1,
        down: 0,
        totalCount: 4,
        history: slightlyWobblyHistory,
        uptimePercent30d: 99.71,
        services: buildServiceList("maplayer-buildings", 4, {
          degradedAt: [2],
          provider: "Kartverket",
        }),
      },
      {
        id: "gis-map-layers-zoning",
        platformId: "placepoint-v2",
        name: "Zoning",
        description: "Municipal zoning plans and overlays",
        status: "operational",
        summary: "All systems operational",
        operational: 3,
        degraded: 0,
        down: 0,
        totalCount: 3,
        history: allOperational,
        uptimePercent30d: 99.95,
        services: buildServiceList("maplayer-zoning", 3, {
          provider: "Kartverket",
        }),
      },
    ],
  },
  {
    id: "placepoint-v2-cloud",
    platformId: "placepoint-v2",
    name: "Microsoft Azure",
    description:
      "Azure services Placepoint runs on (sign-in, resource manager, …)",
    status: "operational",
    summary: "All systems operational",
    operational: 4,
    degraded: 0,
    down: 0,
    totalCount: 4,
    history: allOperational,
    uptimePercent30d: 99.99,
    services: buildServiceList("azure", 4, { provider: "Microsoft Azure" }),
  },
  {
    id: "placepoint-v1-apps",
    platformId: "placepoint-v1",
    name: "Apps",
    description: "The legacy Placepoint v1 web app and its supporting services",
    status: "operational",
    summary: "All systems operational",
    operational: 4,
    degraded: 0,
    down: 0,
    totalCount: 4,
    history: allOperational,
    uptimePercent30d: 99.95,
    services: buildServiceList("v1-apps", 4, { provider: "Placepoint" }),
  },
  {
    id: "propcloud-apps",
    platformId: "propcloud",
    name: "Apps",
    description: "Propcloud apps, reports, and partner-facing experiences",
    status: "operational",
    summary: "All systems operational",
    operational: 8,
    degraded: 0,
    down: 0,
    totalCount: 8,
    history: allOperational,
    uptimePercent30d: 99.92,
    services: buildServiceList("propcloud", 8, { provider: "Propcloud" }),
  },
  {
    id: "propcloud-cloud",
    platformId: "propcloud",
    name: "Amazon Web Services",
    description: "AWS services Propcloud runs on (sign-in, S3, edge, …)",
    status: "operational",
    summary: "All systems operational",
    operational: 4,
    degraded: 0,
    down: 0,
    totalCount: 4,
    history: allOperational,
    uptimePercent30d: 99.97,
    services: buildServiceList("aws", 4, {
      provider: "Amazon Web Services",
    }),
  },
  {
    id: "newsecmaps-api",
    platformId: "newsecmaps",
    name: "API",
    description: "Newsecmaps API health and supporting services",
    status: "operational",
    summary: "All systems operational",
    operational: 1,
    degraded: 0,
    down: 0,
    totalCount: 1,
    history: allOperational,
    uptimePercent30d: 99.88,
    services: buildServiceList("newsecmaps", 1, { provider: "Newsecmaps" }),
  },
  {
    id: "newsecmaps-deps",
    platformId: "newsecmaps",
    name: "Map Service Dependencies",
    description: "Third-party map services Newsecmaps depends on",
    status: "operational",
    summary: "All systems operational",
    operational: 13,
    degraded: 0,
    down: 0,
    totalCount: 13,
    history: allOperational,
    uptimePercent30d: 99.91,
    services: buildServiceList("newsecmaps-deps", 13, { provider: "NVE" }),
  },
  {
    id: "newsecmaps-cloud",
    platformId: "newsecmaps",
    name: "Google Cloud Platform",
    description:
      "GCP / Firebase services Newsecmaps runs on (identity, hosting, …)",
    status: "operational",
    summary: "All systems operational",
    operational: 5,
    degraded: 0,
    down: 0,
    totalCount: 5,
    history: allOperational,
    uptimePercent30d: 99.96,
    services: buildServiceList("gcp", 5, {
      provider: "Google Cloud Platform",
    }),
  },
]

// Per-platform card metadata. Order here is the render order on the
// page; mirrors `publicPlatforms` in the Go backend so the demo
// fixture exercises the same shape the API serves.
//
// Declared *before* DEMO_STATUS because DEMO_STATUS's initializer
// calls buildPlatformsFromCategories(...) which reads DEMO_PLATFORMS.
// Function-declaration hoisting saves the function reference but
// not the const it reads, so DEMO_PLATFORMS would otherwise still
// be in the temporal dead zone at the moment DEMO_STATUS evaluates.
const DEMO_PLATFORMS: ReadonlyArray<{
  id: string
  name: string
  description: string
}> = [
  {
    id: "placepoint-v2",
    name: "Placepoint",
    description:
      "The current Placepoint platform: apps, GIS, and customer experiences",
  },
  {
    id: "placepoint-v1",
    name: "Placepoint v1",
    description:
      "The legacy Placepoint platform still serving a small number of customers",
  },
  {
    id: "propcloud",
    name: "Propcloud",
    description: "Propcloud apps, reports, and partner-facing experiences",
  },
  {
    id: "newsecmaps",
    name: "Newsecmaps",
    description: "Newsecmaps API and supporting services",
  },
]

export const DEMO_STATUS: PublicStatusResponse = {
  summary: buildSummary({
    // 71 (existing) + 4 Azure + 4 AWS + 5 GCP cloud rows = 84 total.
    totalServices: 84,
    servicesUp: 83,
    servicesDown: 0,
    servicesDegraded: 1,
    coreTotalServices: 22,
    coreServicesUp: 22,
    coreServicesDown: 0,
    avgLatencyMs: 312,
    avgLatencyIsBaseline: false,
    uptimePercent30d: 99.97,
    incidentsLast30d: 2,
    incidentsOngoing: 0,
    lastIncidentAt: daysAgoAt(1, 0, 0),
  }),
  platforms: buildPlatformsFromCategories(baseCategories),
  categories: baseCategories,
  providers: [
    {
      id: "geodata",
      name: "Geodata",
      status: "operational",
      summary: "All systems operational",
      operational: 42,
      degraded: 0,
      down: 0,
      totalCount: 42,
      history: allOperational,
    },
    {
      id: "kartverket",
      name: "Kartverket",
      status: "degraded",
      summary: "1 of 12 slower than usual",
      operational: 11,
      degraded: 1,
      down: 0,
      totalCount: 12,
      history: slightlyWobblyHistory,
    },
    {
      id: "miljodirektoratet",
      name: "Miljødirektoratet",
      status: "operational",
      summary: "All systems operational",
      operational: 6,
      degraded: 0,
      down: 0,
      totalCount: 6,
      history: allOperational,
    },
    {
      id: "placepoint",
      name: "Placepoint",
      status: "operational",
      summary: "All systems operational",
      operational: 22,
      degraded: 0,
      down: 0,
      totalCount: 22,
      history: allOperational,
    },
    {
      id: "microsoft-azure",
      name: "Microsoft Azure",
      status: "operational",
      summary: "All systems operational",
      operational: 4,
      degraded: 0,
      down: 0,
      totalCount: 4,
      history: allOperational,
    },
    {
      id: "amazon-web-services",
      name: "Amazon Web Services",
      status: "operational",
      summary: "All systems operational",
      operational: 4,
      degraded: 0,
      down: 0,
      totalCount: 4,
      history: allOperational,
    },
    {
      id: "google-cloud-platform",
      name: "Google Cloud Platform",
      status: "operational",
      summary: "All systems operational",
      operational: 5,
      degraded: 0,
      down: 0,
      totalCount: 5,
      history: allOperational,
    },
  ],
  incidents: [
    {
      id: "demo-1",
      affectedScope: "GIS Map Layers",
      title: "Brief disruption in GIS Map Layers",
      startedAt: yesterdayAt(0, 0),
      endedAt: yesterdayAt(23, 59),
      durationMinutes: 14,
      state: "resolved",
    },
    {
      id: "demo-2",
      affectedScope: "GIS Map Layers",
      title: "2 services in GIS Map Layers experienced a brief disruption",
      startedAt: daysAgoAt(3, 0, 0),
      endedAt: daysAgoAt(3, 23, 59),
      durationMinutes: 6,
      state: "resolved",
    },
  ],
  generatedAt: new Date().toISOString(),
}

// buildSummary fills in the common defaults for the demo
// fixture. Pulled out so the variants above stay focused on the
// fields they actually want to override.
function buildSummary(p: Partial<PublicSummary>): PublicSummary {
  return {
    totalServices: 0,
    servicesUp: 0,
    servicesDown: 0,
    servicesDegraded: 0,
    coreTotalServices: 0,
    coreServicesUp: 0,
    coreServicesDown: 0,
    avgLatencyMs: null,
    avgLatencyIsBaseline: false,
    uptimePercent30d: null,
    incidentsLast30d: 0,
    incidentsOngoing: 0,
    lastIncidentAt: undefined,
    ...p,
  }
}

// buildPlatformsFromCategories rolls the demo categories up into
// PublicPlatform cards exactly the way the Go backend would. The
// fixture is the only place we run this client-side -- live data
// already arrives with platforms baked in.
function buildPlatformsFromCategories(
  categories: PublicCategoryStatus[],
): PublicPlatform[] {
  const byPlatform = new Map<string, PublicCategoryStatus[]>()
  for (const c of categories) {
    const list = byPlatform.get(c.platformId) ?? []
    list.push(c)
    byPlatform.set(c.platformId, list)
  }

  return DEMO_PLATFORMS.map((meta) => {
    const cats = byPlatform.get(meta.id) ?? []
    const totals = cats.reduce(
      (acc, c) => {
        acc.total += c.totalCount
        acc.up += c.operational
        acc.degraded += c.degraded
        acc.down += c.down
        return acc
      },
      { total: 0, up: 0, degraded: 0, down: 0 },
    )
    const status = pickPlatformStatus(totals)
    return {
      id: meta.id,
      name: meta.name,
      description: meta.description,
      status,
      summary: pickPlatformSummary(totals),
      operational: totals.up,
      degraded: totals.degraded,
      down: totals.down,
      totalCount: totals.total,
      uptimePercent30d:
        totals.total === 0 ? null : status === "operational" ? 99.95 : 99.6,
      history: cats.length > 0 ? cats[0].history : allOperational,
      categoryIds: cats.map((c) => c.id),
    }
  })
}

function pickPlatformStatus(t: {
  down: number
  degraded: number
  total: number
}): "operational" | "degraded" | "down" {
  return pickAggregateStatus(t.down, t.degraded, t.total)
}

// pickAggregateStatus mirrors the backend's `aggregateStatus` rule
// (apps/backends/v2-status-api/src/publicstatus/status.go): red is
// reserved for the all-down catastrophic case, amber covers any
// partial outage, green for fully healthy / empty groups. Used by
// every group-level demo computation so the fixture matches what
// the live API would emit for the same shape.
function pickAggregateStatus(
  down: number,
  degraded: number,
  total: number,
): "operational" | "degraded" | "down" {
  if (total <= 0) return "operational"
  if (down >= total) return "down"
  if (down > 0 || degraded > 0) return "degraded"
  return "operational"
}

function pickPlatformSummary(t: {
  down: number
  degraded: number
  total: number
}): string {
  if (t.total === 0) return "No services configured yet"
  if (t.down > 0) return `${t.down} of ${t.total} unavailable`
  if (t.degraded > 0) return `${t.degraded} of ${t.total} slower than usual`
  return "All systems operational"
}

function yesterdayAt(h: number, m: number): string {
  return daysAgoAt(1, h, m)
}

function daysAgoAt(days: number, h: number, m: number): string {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - days)
  d.setUTCHours(h, m, 0, 0)
  return d.toISOString()
}

// Variant fixtures so a reviewer can preview every KPI shape
// without writing code. We model each variant as a real-world
// scenario:
//
//   ?demo=partial  - a couple of core apps unhappy, a few upstream
//                    layers blipping. KPI strip flags the core
//                    apps tile and the incidents tile.
//   ?demo=major    - widespread core outage; nearly every tile
//                    carries critical/warning copy.
//   ?demo=1        - all green (DEMO_STATUS above).
//
// The same DOM tree renders across all three so the page shape is
// stable between previews.
function withOutage(level: "partial" | "major"): typeof DEMO_STATUS {
  const coreDown = level === "major" ? 6 : 2
  const coreDegraded = level === "major" ? 2 : 1
  const upstreamDown = level === "major" ? 3 : 2

  const categories = DEMO_STATUS.categories.map((c) => {
    if (c.id === "placepoint-v2-apps") {
      return mutateCategoryServices(c, {
        // Aggregate rows always read amber for a partial outage --
        // even the "major" demo is only ~30% of services down, not
        // 100%, so the dot stays at "degraded" to match the live
        // rule in the backend `aggregateStatus`.
        status: pickAggregateStatus(coreDown, coreDegraded, c.totalCount),
        downCount: coreDown,
        degradedCount: coreDegraded,
        provider: "Placepoint",
        prefix: "apps",
      })
    }
    if (c.id === "gis-map-layers") {
      return mutateCategoryServices(c, {
        status: pickAggregateStatus(upstreamDown, 0, c.totalCount),
        downCount: upstreamDown,
        degradedCount: 0,
        provider: "Kartverket",
        prefix: "maplayer",
      })
    }
    return c
  })

  const totals = categories.reduce(
    (acc, c) => {
      acc.total += c.totalCount
      acc.up += c.operational
      acc.down += c.down
      acc.degraded += c.degraded
      return acc
    },
    { total: 0, up: 0, down: 0, degraded: 0 },
  )

  return {
    ...DEMO_STATUS,
    summary: buildSummary({
      totalServices: totals.total,
      servicesUp: totals.up,
      servicesDown: totals.down,
      servicesDegraded: totals.degraded,
      coreTotalServices: 22,
      coreServicesUp: 22 - coreDown,
      coreServicesDown: coreDown,
      avgLatencyMs: level === "major" ? 612 : 386,
      avgLatencyIsBaseline: false,
      uptimePercent30d: level === "major" ? 96.18 : 98.72,
      incidentsLast30d: level === "major" ? 7 : 3,
      incidentsOngoing: level === "major" ? 2 : 1,
      lastIncidentAt: new Date().toISOString(),
    }),
    platforms: buildPlatformsFromCategories(categories),
    categories,
  }
}

// mutateCategoryServices is a small helper used by the demo
// outage variants to swap a category's services for a fixture
// that exercises the down/degraded states. Pulled out so the
// outage builder above stays readable -- the alternative is a
// 30-line nested map inside `withOutage`.
function mutateCategoryServices(
  c: PublicCategoryStatus,
  opts: {
    status: PublicCategoryStatus["status"]
    downCount: number
    degradedCount: number
    provider: string
    prefix: string
  },
): PublicCategoryStatus {
  const { status, downCount, degradedCount, provider, prefix } = opts
  const downAt = Array.from({ length: downCount }, (_, i) => i)
  const degradedAt = Array.from(
    { length: degradedCount },
    (_, i) => downCount + i,
  )
  const summary =
    downCount > 0
      ? `${downCount} of ${c.totalCount} unavailable`
      : `${degradedCount} of ${c.totalCount} slower than usual`
  return {
    ...c,
    status,
    summary,
    operational: c.totalCount - downCount - degradedCount,
    degraded: degradedCount,
    down: downCount,
    services: buildServiceList(prefix, c.totalCount, {
      degradedAt,
      downAt,
      provider,
    }),
  }
}

export function getDemoFixture(): typeof DEMO_STATUS {
  if (typeof window === "undefined") return DEMO_STATUS
  const v = new URLSearchParams(window.location.search).get("demo")
  if (v === "partial") return withOutage("partial")
  if (v === "major") return withOutage("major")
  return DEMO_STATUS
}

// True when the page should render against the demo fixture instead
// of fetching the public API. Driven by `?demo=…` in the URL so a
// stakeholder review or screenshot session can be a one-liner.
export function isDemoMode(): boolean {
  if (typeof window === "undefined") return false
  const v = new URLSearchParams(window.location.search).get("demo")
  return v === "1" || v === "partial" || v === "major"
}
